// 📁 src/admin/ContactEnquiries.js
import React, { useEffect, useMemo, useState } from "react";
import { api } from "../axiosClient";
import { toast } from "react-toastify";

const ContactEnquiries = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters + UI state
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    name: "",
    email: "",
    company: "",
    country: "",
    reason: "",
    bulk: "all", // all | yes | no
  });
  const [sort, setSort] = useState("newest"); // newest | oldest
  const [selected, setSelected] = useState(null);

  const fetchContacts = async () => {
    try {
      // ✅ use token saved inside userInfo
      const userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}");
      const token = userInfo.token;

      if (!token) {
        toast.error("Please login again (token missing)");
        setLoading(false);
        return;
      }

      const res = await api.get("/api/contact"); 

      setContacts(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load contact enquiries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();

    const matchesSearch = (c) => {
      if (!s) return true;
      const hay = [
        c?.name,
        c?.email,
        c?.company,
        c?.country,
        c?.reason,
        c?.message,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(s);
    };

    const matchesFilters = (c) => {
      const f = filters;
      const includes = (value, query) => {
        if (!query) return true;
        return String(value || "").toLowerCase().includes(query.toLowerCase());
      };

      if (!includes(c?.name, f.name)) return false;
      if (!includes(c?.email, f.email)) return false;
      if (!includes(c?.company, f.company)) return false;
      if (!includes(c?.country, f.country)) return false;
      if (!includes(c?.reason, f.reason)) return false;

      if (f.bulk !== "all") {
        const isBulk = !!c?.isBulk;
        if (f.bulk === "yes" && !isBulk) return false;
        if (f.bulk === "no" && isBulk) return false;
      }

      return true;
    };

    const rows = contacts
      .filter((c) => matchesSearch(c) && matchesFilters(c))
      .sort((a, b) => {
        const da = new Date(a?.createdAt || 0).getTime();
        const db = new Date(b?.createdAt || 0).getTime();
        return sort === "newest" ? db - da : da - db;
      });

    return rows;
  }, [contacts, search, filters, sort]);

  const clearFilters = () => {
    setSearch("");
    setFilters({
      name: "",
      email: "",
      company: "",
      country: "",
      reason: "",
      bulk: "all",
    });
    setSort("newest");
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-[#105132]">
            Contact Form Enquiries
          </h2>
          <p className="text-sm text-gray-600">
            Total: <span className="font-semibold">{contacts.length}</span> •
            Showing: <span className="font-semibold">{filtered.length}</span>
          </p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={fetchContacts}
            className="px-4 py-2 rounded-lg bg-white border border-gray-200 shadow-sm hover:shadow transition"
          >
            Refresh
          </button>
          <button
            onClick={clearFilters}
            className="px-4 py-2 rounded-lg bg-[#105132] text-white hover:opacity-95 transition"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <label className="text-xs font-semibold text-gray-600">
              Search (all fields)
            </label>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, email, country, reason, message..."
              className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#105132]/30"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-600">
              Sort by date
            </label>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 bg-white"
            >
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
            </select>
          </div>
        </div>

        {/* Column filters */}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-6 gap-3">
          <div>
            <label className="text-xs font-semibold text-gray-600">Name</label>
            <input
              value={filters.name}
              onChange={(e) => setFilters((p) => ({ ...p, name: e.target.value }))}
              className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2"
              placeholder="Filter..."
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600">Email</label>
            <input
              value={filters.email}
              onChange={(e) =>
                setFilters((p) => ({ ...p, email: e.target.value }))
              }
              className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2"
              placeholder="Filter..."
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600">Company</label>
            <input
              value={filters.company}
              onChange={(e) =>
                setFilters((p) => ({ ...p, company: e.target.value }))
              }
              className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2"
              placeholder="Filter..."
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600">Country</label>
            <input
              value={filters.country}
              onChange={(e) =>
                setFilters((p) => ({ ...p, country: e.target.value }))
              }
              className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2"
              placeholder="Filter..."
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600">Reason</label>
            <input
              value={filters.reason}
              onChange={(e) =>
                setFilters((p) => ({ ...p, reason: e.target.value }))
              }
              className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2"
              placeholder="Filter..."
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600">Bulk</label>
            <select
              value={filters.bulk}
              onChange={(e) =>
                setFilters((p) => ({ ...p, bulk: e.target.value }))
              }
              className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 bg-white"
            >
              <option value="all">All</option>
              <option value="yes">Bulk only</option>
              <option value="no">Non-bulk</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-6 text-gray-600">Loading enquiries...</div>
        ) : filtered.length === 0 ? (
          <div className="p-6 text-gray-600">
            No enquiries match your filters.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-[#105132] text-white">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Name</th>
                  <th className="text-left px-4 py-3 font-semibold">Email</th>
                  <th className="text-left px-4 py-3 font-semibold">Company</th>
                  <th className="text-left px-4 py-3 font-semibold">Country</th>
                  <th className="text-left px-4 py-3 font-semibold">Reason</th>
                  <th className="text-left px-4 py-3 font-semibold">Bulk</th>
                  <th className="text-left px-4 py-3 font-semibold">Date</th>
                  <th className="text-right px-4 py-3 font-semibold">Action</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100">
                {filtered.map((c) => (
                  <tr key={c._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">
                      {c.name}
                    </td>
                    <td className="px-4 py-3 text-gray-700">{c.email}</td>
                    <td className="px-4 py-3 text-gray-700">
                      {c.company || "-"}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {c.country || "-"}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {c.reason || "-"}
                    </td>
                    <td className="px-4 py-3">
                      {c.isBulk ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-red-50 text-red-700 border border-red-100">
                          Yes
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-gray-50 text-gray-700 border border-gray-100">
                          No
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {c.createdAt
                        ? new Date(c.createdAt).toLocaleString()
                        : "-"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => setSelected(c)}
                        className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:shadow-sm transition"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div className="bg-white w-full max-w-2xl rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 bg-[#f3f0e4]">
              <div>
                <h3 className="text-lg font-semibold text-[#105132]">
                  {selected.name}
                </h3>
                <p className="text-sm text-gray-600">{selected.email}</p>
              </div>
              <button
                onClick={() => setSelected(null)}
                className="text-gray-600 hover:text-gray-900 text-2xl leading-none"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="p-5 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Info label="Company" value={selected.company || "-"} />
                <Info label="Country" value={selected.country || "-"} />
                <Info label="Reason" value={selected.reason || "-"} />
                <Info label="Bulk" value={selected.isBulk ? "Yes" : "No"} />
              </div>

              <div className="pt-2">
                <p className="text-xs font-semibold text-gray-600">Message</p>
                <div className="mt-1 border border-gray-200 rounded-xl p-4 bg-white whitespace-pre-line text-gray-800">
                  {selected.message || "-"}
                </div>
              </div>

              <p className="text-xs text-gray-500">
                Submitted on{" "}
                {selected.createdAt
                  ? new Date(selected.createdAt).toLocaleString()
                  : "-"}
              </p>
            </div>

            <div className="px-5 py-4 flex justify-end gap-2 border-t border-gray-100">
              <button
                onClick={() => setSelected(null)}
                className="px-4 py-2 rounded-lg bg-white border border-gray-200 hover:shadow-sm transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Info = ({ label, value }) => (
  <div className="border border-gray-200 rounded-xl p-3 bg-white">
    <p className="text-xs font-semibold text-gray-600">{label}</p>
    <p className="text-sm font-medium text-gray-900 mt-1">{value}</p>
  </div>
);

export default ContactEnquiries;
